import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { useGoogleCalendarIntegration } from "@/contexts/google-calendar-integration-provider";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { useOutlookCalendarIntegration } from "@/contexts/outlook-calendar-integration-provider";
import { useSeliarisIntegration } from "@/contexts/seliaris-integration-provider";
import {
  detectBrowser,
  useChromeExtensionStatus,
} from "@/hooks/useChromeExtensionStatus";
import type { Integration } from "@/lib/integrations";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface IntegrationCardProps {
  integration: Integration;
  onEarlyAccess: (integrationName: string) => void;
}

interface BaseCardProps {
  integration: Integration;
}

export const IntegrationCard = ({
  integration,
  onEarlyAccess,
}: IntegrationCardProps) => {
  const navigate = useNavigate();
  const { hubspotIntegration } = useHubSpotIntegration();
  const { googleCalendarIntegration } = useGoogleCalendarIntegration();
  const { isInstalled: isExtensionInstalled, isChecking: isCheckingExtension } =
    useChromeExtensionStatus();
  const currentBrowser = detectBrowser();
  const { outlookCalendarIntegration } = useOutlookCalendarIntegration();
  const { seliarisIntegration } = useSeliarisIntegration();

  const handleNavigate = () => {
    if (integration.link) {
      navigate(integration.link);
    }
  };

  const handleEarlyAccess = () => {
    onEarlyAccess(integration.title);
  };

  // Check if this is a browser extension
  const isBrowserExtension =
    integration.key === "chromeExtension" ||
    integration.key === "edgeExtension";

  // Determine if extension status badge should be shown for this integration
  const shouldShowExtensionStatus =
    (integration.key === "chromeExtension" && currentBrowser === "chrome") ||
    (integration.key === "edgeExtension" && currentBrowser === "edge");

  if (integration.key === "hubspot" && hubspotIntegration?.isActive) {
    // The connection is still there but HubSpot rejected its grant: it serves
    // nothing until the user goes through OAuth again.
    if (!hubspotIntegration.isAvailable) {
      return (
        <ExpiredIntegrationCard
          integration={integration}
          onReconnect={handleNavigate}
        />
      );
    }

    return (
      <ConnectedIntegrationCard
        integration={integration}
        onManage={handleNavigate}
      />
    );
  }

  if (integration.key === "seliaris" && seliarisIntegration?.isActive) {
    return (
      <ConnectedIntegrationCard
        integration={integration}
        onManage={handleNavigate}
      />
    );
  }

  if (integration.key === "googleCalendar" && googleCalendarIntegration) {
    return (
      <ConnectedIntegrationCard
        integration={integration}
        onManage={handleNavigate}
      />
    );
  }

  if (integration.key === "outlookCalendar" && outlookCalendarIntegration) {
    return (
      <ConnectedIntegrationCard
        integration={integration}
        onManage={handleNavigate}
      />
    );
  }

  if (integration.available) {
    return (
      <AvailableIntegrationCard
        integration={integration}
        onConnect={handleNavigate}
        isExtensionInstalled={
          isBrowserExtension ? isExtensionInstalled : undefined
        }
        isCheckingExtension={
          isBrowserExtension ? isCheckingExtension : undefined
        }
        shouldShowExtensionStatus={shouldShowExtensionStatus}
      />
    );
  }

  return (
    <ComingSoonIntegrationCard
      integration={integration}
      onEarlyAccess={handleEarlyAccess}
    />
  );
};

const BaseIntegrationCard = ({
  integration,
  badge,
  button,
}: BaseCardProps & {
  badge: React.ReactNode;
  button: React.ReactNode;
}) => (
  <Card className="flex flex-col transition-shadow cursor-default hover:shadow-md">
    <CardContent className="flex flex-col flex-1 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {integration.logo && (
            <div className="flex items-center justify-center shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-slate-700">
              <img
                src={integration.logo}
                alt={`${integration.title} logo`}
                className="w-6 h-6"
              />
            </div>
          )}
          <div className="flex flex-col justify-between gap-2">
            <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-50">
              {integration.title}
            </CardTitle>
            <div className="flex gap-2">{badge}</div>
          </div>
        </div>
      </div>
      <CardDescription className="mb-4 text-sm text-gray-600 dark:text-gray-400 flex-1">
        {integration.description}
      </CardDescription>
      {button}
    </CardContent>
  </Card>
);

// Component for connected integrations
const ConnectedIntegrationCard = ({
  integration,
  onManage,
}: BaseCardProps & { onManage: () => void }) => {
  const { t } = useTranslation();

  return (
    <BaseIntegrationCard
      integration={integration}
      badge={
        <div className="flex gap-2">
          <Badge
            variant="secondary"
            className="text-xs text-green-800 bg-green-100 border-green-200 dark:text-green-200 dark:bg-green-950 dark:border-green-800"
          >
            {t("team.integrations.card.connected")}
          </Badge>
          {integration.beta && (
            <Badge
              variant="secondary"
              className="text-xs text-green-800 bg-green-100 border-green-200 dark:text-green-200 dark:bg-green-950 dark:border-green-800"
            >
              {t("team.integrations.card.beta")}
            </Badge>
          )}
        </div>
      }
      button={
        <Button
          variant="default"
          size="sm"
          className="w-full"
          onClick={onManage}
        >
          {t("team.integrations.card.manage")}
        </Button>
      }
    />
  );
};

// Component for connected integrations whose authorization expired
const ExpiredIntegrationCard = ({
  integration,
  onReconnect,
}: BaseCardProps & { onReconnect: () => void }): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <BaseIntegrationCard
      integration={integration}
      badge={
        <div className="flex gap-2">
          <Badge
            variant="secondary"
            className="text-xs text-amber-800 bg-amber-100 border-amber-200 dark:text-amber-200 dark:bg-amber-950 dark:border-amber-800"
          >
            {t("team.integrations.card.expired")}
          </Badge>
          {integration.beta && (
            <Badge
              variant="secondary"
              className="text-xs text-green-800 bg-green-100 border-green-200 dark:text-green-200 dark:bg-green-950 dark:border-green-800"
            >
              {t("team.integrations.card.beta")}
            </Badge>
          )}
        </div>
      }
      button={
        <Button
          variant="default"
          size="sm"
          className="w-full"
          onClick={onReconnect}
        >
          {t("team.integrations.card.reconnect")}
        </Button>
      }
    />
  );
};

// Component for available integrations
const AvailableIntegrationCard = ({
  integration,
  onConnect,
  isExtensionInstalled,
  isCheckingExtension,
  shouldShowExtensionStatus,
}: BaseCardProps & {
  onConnect: () => void;
  isExtensionInstalled?: boolean;
  isCheckingExtension?: boolean;
  shouldShowExtensionStatus?: boolean;
}) => {
  const { t } = useTranslation();

  // Check if the integration is a browser extension
  const isExtension =
    integration.key === "chromeExtension" ||
    integration.key === "edgeExtension";

  const getExtensionStatusBadge = () => {
    if (isCheckingExtension) {
      return (
        <Badge
          variant="outline"
          className="text-xs text-gray-600 border-gray-300"
        >
          {t("common.loading")}
        </Badge>
      );
    }

    if (isExtensionInstalled) {
      return (
        <Badge
          variant="secondary"
          className="text-xs text-green-800 bg-green-100 border-green-200 dark:text-green-200 dark:bg-green-950 dark:border-green-800"
        >
          {t("team.integrations.chromeExtension.connected")}
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-xs">
        {t("team.integrations.chromeExtension.notConnected")}
      </Badge>
    );
  };

  return (
    <BaseIntegrationCard
      integration={integration}
      badge={
        <div className="flex gap-2">
          {integration.beta && (
            <Badge
              variant="secondary"
              className="text-xs text-green-800 bg-green-100 border-green-200 dark:text-green-200 dark:bg-green-950 dark:border-green-800"
            >
              {t("team.integrations.card.beta")}
            </Badge>
          )}
          {isExtension &&
            shouldShowExtensionStatus &&
            isExtensionInstalled !== undefined &&
            getExtensionStatusBadge()}
        </div>
      }
      button={
        <Button
          variant="default"
          size="sm"
          className="w-full"
          onClick={onConnect}
        >
          {isExtension
            ? shouldShowExtensionStatus && !isExtensionInstalled
              ? t("team.integrations.card.install")
              : t("team.integrations.card.viewMore")
            : t("team.integrations.card.connect")}
        </Button>
      }
    />
  );
};

// Component for coming soon integrations
const ComingSoonIntegrationCard = ({
  integration,
  onEarlyAccess,
}: BaseCardProps & { onEarlyAccess: () => void }) => {
  const { t } = useTranslation();

  return (
    <BaseIntegrationCard
      integration={integration}
      badge={
        <Badge
          variant="secondary"
          className="text-xs text-gray-600 bg-gray-100 border-gray-200 dark:text-gray-300 dark:bg-slate-700 dark:border-slate-600"
        >
          {t("team.integrations.card.soon")}
        </Badge>
      }
      button={
        <Button
          variant="secondary"
          size="sm"
          className="w-full"
          onClick={onEarlyAccess}
        >
          {t("team.integrations.card.earlyAccess")}
        </Button>
      }
    />
  );
};
